import { memo } from 'react';
import type { FormFieldType } from '@/services/api-types';
import type { FieldDraft } from './schema-draft';
import { FieldCommon, SCALAR_FIELD_TYPES } from './FieldCommon';
import { OptionsEditor } from './OptionsEditor';
import { isOptionType } from './schema-draft';

export interface ColumnsEditorProps {
  columns: FieldDraft[];
  onAdd: () => void;
  onRemove: (columnUiId: string) => void;
  onMove: (columnUiId: string, delta: 1 | -1) => void;
  onUpdate: (columnUiId: string, patch: Partial<FieldDraft>) => void;
  onTypeChange: (columnUiId: string, type: FormFieldType) => void;
  onAddOption: (columnUiId: string) => void;
  onRemoveOption: (columnUiId: string, idx: number) => void;
  onMoveOption: (columnUiId: string, idx: number, delta: 1 | -1) => void;
  onUpdateOption: (columnUiId: string, idx: number, patch: Partial<{ value: string; label: string }>) => void;
}

export const ColumnsEditor = memo(function ColumnsEditor({
  columns,
  onAdd,
  onRemove,
  onMove,
  onUpdate,
  onTypeChange,
  onAddOption,
  onRemoveOption,
  onMoveOption,
  onUpdateOption,
}: ColumnsEditorProps) {
  return (
    <div className="kst-fb-columns">
      <div className="kst-fb-columns-title">Columns</div>
      {columns.length === 0 && <div className="kst-fb-empty">No columns yet. Click "Add column" to add the first one.</div>}
      {columns.map((col, i) => (
        <div key={col.uiId} className="kst-fb-column-card">
          <div className="kst-fb-column-header">
            <span className="kst-fb-column-title">Column {i + 1}</span>
            <span className="kst-fb-actions">
              <button type="button" className="kstc-btn kstc-btn--secondary" onClick={() => onMove(col.uiId, -1)} disabled={i === 0}>↑</button>
              <button type="button" className="kstc-btn kstc-btn--secondary" onClick={() => onMove(col.uiId, 1)} disabled={i === columns.length - 1}>↓</button>
              <button type="button" className="kstc-btn kstc-btn--danger" onClick={() => onRemove(col.uiId)}>×</button>
            </span>
          </div>
          <FieldCommon
            field={col}
            typeOptions={SCALAR_FIELD_TYPES}
            hideFlags
            onLabelChange={(label) => onUpdate(col.uiId, { label })}
            onKeyChange={(key, locked) => onUpdate(col.uiId, { key, keyLocked: locked })}
            onTypeChange={(type) => onTypeChange(col.uiId, type)}
            onFlagChange={() => { /* flags hidden for columns */ }}
          />
          {isOptionType(col.type) && (
            <OptionsEditor
              options={col.options ?? []}
              onAdd={() => onAddOption(col.uiId)}
              onRemove={(idx) => onRemoveOption(col.uiId, idx)}
              onMove={(idx, delta) => onMoveOption(col.uiId, idx, delta)}
              onUpdate={(idx, patch) => onUpdateOption(col.uiId, idx, patch)}
            />
          )}
        </div>
      ))}
      <button type="button" className="kstc-btn kstc-btn--secondary" onClick={onAdd} style={{ marginTop: 6 }}>
        + Add column
      </button>
    </div>
  );
});
